import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { AnalyticsService } from '$/services';
import Loading from '$/components/loading/Loading';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import MailToCellRenderer from '$/components/global/mailToCellRenderer';
import './index.scss';
import Modal from '$/components/global/modal';
import DataSitesLegend from '$/components/global/dataSitesLegend';

interface INonMnpiClientDataSiteProps {
  client?: Bixby.Models.Client;
  isLoading: boolean;
  profile?: Bixby.Models.Profile;
}

const NonMnpiClientDataSite: React.FC<INonMnpiClientDataSiteProps> = props => {
  const { client, isLoading, profile } = props;
  const [, setDataSiteGridApi] = useState(null as GridApi | null);
  const [, setDataSiteGridColumnApi] = useState(null as ColumnApi | null);
  const [, setDataSiteViewGridApi] = useState(null as GridApi | null);
  const [, setDataSiteViewGridColumnApi] = useState(null as ColumnApi | null);

  const [modalActive, setModalActive] = useState(false);

  if (!client) {
    return null;
  }

  const companyIdCellRenderer = (params: ICellRendererParams) => {
    return Bixby.Utils.arrayIsPopulated(params.value) ? (
      <div
        className={classnames('company_links', {
          single_company: params.value.length === 1,
          multiple_companies: params.value.length > 1,
        })}>
        {params.value.map((row: any, index: number) => {
          return (
            <Link key={index} className="link_underline" to={`/admin/company/${row}`} target="_blank">
              Co {index + 1}
            </Link>
          );
        })}
      </div>
    ) : (
      <div></div>
    );
  };

  const handleOpenModal = () => {
    setModalActive(true);
  };

  const handleCloseModal = () => {
    setModalActive(false);
  };

  const asteriskNoteMatch = /\w+[*]/;

  const createFeedbackUrl = (dataSites: Bixby.Models.ClientDataSiteForClient) => {
    const feedbackUrlSubject: string = encodeURIComponent(`Company Request: ${dataSites.dataSiteName}`);

    const user: string =
      profile && profile.email && profile.firstName && profile.lastName
        ? `${profile.firstName} ${profile.lastName} (${profile.email})`
        : 'User';
    let newsFeedbackUrlBody: string;
    if (dataSites.notes === 'Eligible') {
      newsFeedbackUrlBody = encodeURIComponent(
        `${user} would like to add ${dataSites.dataSiteName} to ${dataSites.clientName}'s priority list.`
      );
    } else if (dataSites.notes === 'No USD Loans*') {
      newsFeedbackUrlBody = encodeURIComponent(
        `${user} would like to learn more about Bixby's European coverage add-on package and see the following name on a trial basis: ${dataSites.dataSiteName}.`
      );
    } else if (dataSites.notes === 'Data Site Missing Information' || dataSites.notes === 'Other - contact Bixby') {
      newsFeedbackUrlBody = encodeURIComponent(
        `${user} would like to inquire about the status of ${dataSites.dataSiteName}.`
      );
    } else if (dataSites.notes.match(asteriskNoteMatch)) {
      newsFeedbackUrlBody = encodeURIComponent(
        `${user} would like to inquire about ${dataSites.dataSiteName}. This site doesn’t appear to qualify for Bixby coverage. If you believe this was tagged in error please let us know and a member of the team will respond as soon as possible.`
      );
    } else {
      newsFeedbackUrlBody = encodeURIComponent(`${user} would like to request access to: ${dataSites.dataSiteName}.`);
    }
    return `mailto:feedback@bixbyresearch.com?subject=${feedbackUrlSubject}&body=${newsFeedbackUrlBody}`;
  };

  const createContactLinkText = (dataSites: Bixby.Models.ClientDataSiteForClient) => {
    if (dataSites.notes === 'Eligible') {
      return 'Prioritize';
    } else if (dataSites.notes === 'No USD Loans*') {
      return '*NEW* Bixby Euro Coverage';
    } else if (
      dataSites.notes === 'Data Site Missing Information' ||
      dataSites.notes === 'Other - contact Bixby' ||
      dataSites.notes.match(asteriskNoteMatch)
    ) {
      return 'Contact Bixby';
    } else {
      return 'Request Access';
    }
  };

  const handleFeedbackClick = (row: Bixby.Models.ClientDataSiteForClient) => {
    if (profile) {
      AnalyticsService.event(Bixby.Constants.APPINSIGHTS_FEEDBACK_LINK_CLICK, {
        ...profile,
        type: 'DataSites',
        dataSiteName: row.dataSiteName,
      });
    }
  };

  let clientDataSitesViewHeader: string | null = null;
  if (client.dataSitesForClient && !Bixby.Utils.isNullOrEmpty(client.dataSitesForClient)) {
    const clientDataSiteUploadDate: Date = client.dataSitesForClient[0].uploadDate;
    const uploadDate: string = new Date(clientDataSiteUploadDate).toLocaleDateString();
    clientDataSitesViewHeader = `Data Sites: ${client.dataSitesForClient.length} (last updated ${uploadDate})`;
  }

  const onGridReadyDataSite = (params: any) => {
    setDataSiteGridApi(params.api);
    setDataSiteGridColumnApi(params.columnApi);
  };

  const onGridReadyDataSiteView = (params: any) => {
    setDataSiteViewGridApi(params.api);
    setDataSiteViewGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  return (
    <React.Fragment>
      {isLoading ? (
        <Loading pageLevel />
      ) : (
        <section>
          <div className="client_detail_datasites">
            <div className="profile-input-container">
              <div className="profile-input-column">
                <h3>Non Mnpi Client Data Sites</h3>
                {client.dataSites && !Bixby.Utils.isNullOrEmpty(client.dataSites) ? (
                  <div className="data-sites-access-data-grid">
                    <div className="grid_container">
                      <div className="grid ag-theme-alpine">
                        <AgGridReact
                          enableRangeSelection={false}
                          getContextMenuItems={getContextMenuItems}
                          defaultColDef={{
                            flex: 1,
                            minWidth: 100,
                            sortable: true,
                            resizable: true,
                            lockPinned: true,
                          }}
                          onGridReady={onGridReadyDataSite}
                          rowData={client.dataSites}
                          suppressDragLeaveHidesColumns={true}
                          suppressMovableColumns={true}
                          suppressCellFocus={true}
                          animateRows={true}
                          rowHeight={32}
                          headerHeight={32}
                          components={{
                            companyIdCellRenderer: companyIdCellRenderer,
                          }}
                          columnDefs={[
                            {
                              field: 'dataSiteName',
                              filter: 'agTextColumnFilter',
                              floatingFilter: true,
                              headerName: 'Data Site Name',
                              minWidth: 250,
                              pinned: 'left',
                            },
                            {
                              field: 'companyExistsInLibrary',
                              headerName: 'Library?',
                              minWidth: 150,
                              valueFormatter: (params: any) => {
                                return params.data['companyExistsInLibrary'] ? 'Yes' : 'No';
                              },
                            },
                            {
                              field: 'clientHasAccess',
                              headerName: 'Client?',
                              minWidth: 150,
                              valueFormatter: (params: any) => {
                                return params.data['clientHasAccess'] ? 'Yes' : 'No';
                              },
                            },
                            {
                              field: 'companyIds',
                              headerName: 'Companies',
                              minWidth: 250,
                              cellRenderer: 'companyIdCellRenderer',
                            },
                            {
                              field: 'declaration',
                              filter: 'agTextColumnFilter',
                              headerName: 'Declaration',
                              minWidth: 150,
                              valueFormatter: (params: any) => {
                                return params.data['declaration'];
                              },
                            },
                            {
                              field: 'notes',
                              filter: 'agTextColumnFilter',
                              floatingFilter: true,
                              headerName: 'Status',
                              minWidth: 250,
                            },
                          ]}></AgGridReact>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p>{`There are no data sites found`}</p>
                )}
              </div>
              <div className="profile-input-column client-data-sites">
                <h3>Non Mnpi Client Data Sites View</h3>
                {clientDataSitesViewHeader && (
                  <div>
                    <p className="client_detail_datasites_note">{clientDataSitesViewHeader}</p>
                    <div className="client_detail_datasites_header">
                      <p className="client_detail_datasites_header_note">
                        Note:{' '}
                        <a className="legend-link" onClick={handleOpenModal}>
                          See Legend
                        </a>{' '}
                        for status details; data site access and Bixby status updated daily; will not reflect intraday
                        changes.
                      </p>
                      <p className="client_detail_datasites_header_note">
                        *Not currently covered by Bixby; please reach out for further information.
                      </p>
                    </div>
                  </div>
                )}
                {client.dataSitesForClient && !Bixby.Utils.isNullOrEmpty(client.dataSitesForClient) ? (
                  <div className="data-sites-access-data-grid">
                    <div className="grid_container">
                      <div className="grid ag-theme-alpine">
                        <AgGridReact
                          enableRangeSelection={false}
                          getContextMenuItems={getContextMenuItems}
                          defaultColDef={{
                            flex: 1,
                            minWidth: 100,
                            sortable: true,
                            resizable: true,
                            lockPinned: true,
                          }}
                          onGridReady={onGridReadyDataSiteView}
                          rowData={client.dataSitesForClient}
                          suppressDragLeaveHidesColumns={true}
                          suppressMovableColumns={true}
                          animateRows={true}
                          rowHeight={32}
                          headerHeight={32}
                          components={{
                            mailToCellRenderer: MailToCellRenderer,
                          }}
                          columnDefs={[
                            {
                              field: 'dataSiteName',
                              filter: 'agTextColumnFilter',
                              floatingFilter: true,
                              headerName: 'Data Site Name',
                              minWidth: 250,
                              pinned: 'left',
                            },
                            {
                              field: 'companyName',
                              filter: 'agTextColumnFilter',
                              floatingFilter: true,
                              headerName: 'Bixby Access (Company)',
                              minWidth: 250,
                              cellRenderer: (params: any) => {
                                //In CSCP Bixby Admin don't provide a link to a non-accessible area.
                                if (Bixby.Configuration.isStandAloneMode && params.data['companyId']) {
                                  return (
                                    <a
                                      className="bix-link link_underline"
                                      href={`/company/${params.data['companyId']}`}>
                                      {params.data['companyName']}
                                    </a>
                                  );
                                } else {
                                  return (
                                    <div title={`${params.data['companyName']}`}> {params.data['companyName']} </div>
                                  );
                                }
                              },
                            },
                            {
                              field: 'openFigiTicker',
                              filter: 'agTextColumnFilter',
                              floatingFilter: true,
                              headerName: 'Loan Ticker',
                              minWidth: 130,
                            },
                            {
                              field: 'dataSiteEmail',
                              filter: 'agTextColumnFilter',
                              headerName: 'Data Site Email',
                              minWidth: 200,
                            },
                            {
                              field: 'notes',
                              filter: 'agTextColumnFilter',
                              floatingFilter: true,
                              headerName: 'Status',
                              minWidth: 250,
                            },
                            {
                              field: 'allowInquiry',
                              headerName: 'Prioritize / Contact Bixby',
                              minWidth: 250,
                              cellRenderer: 'mailToCellRenderer',
                              cellRendererParams: {
                                show: (params: ICellRendererParams) => {
                                  return params.data['allowInquiry'] && params.data.notes !== 'Available on Bixby';
                                },
                                createFeedbackUrl: (params: Bixby.Models.ClientDataSiteForClient) =>
                                  createFeedbackUrl(params),
                                handleFeedbackClick: (params: Bixby.Models.ClientDataSiteForClient) =>
                                  handleFeedbackClick(params),
                                createContactLinkText: (params: Bixby.Models.ClientDataSiteForClient) =>
                                  createContactLinkText(params),
                              },
                            },
                          ]}></AgGridReact>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p>{`There are no data sites found`}</p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
      <Modal title="Data Site Status Legend" active={modalActive} hideClose={true}>
        <DataSitesLegend onCloseModal={handleCloseModal} />
      </Modal>
    </React.Fragment>
  );
};

const mapDispatchToProps = () => ({});

const mapStateToProps = (state: AppState) => {
  return {
    client: state.clientState.currentClient,
    isLoading: state.clientState.isLoading,
    profile: state.profileState.profile,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(NonMnpiClientDataSite);
